/*eslint-disable no-eval*/
import { AutocompleteChangeDetails, AutocompleteChangeReason, Box, MenuItem, Stack } from "@mui/material";
import { useOfflineDetectContext } from "../../contexts/offline-detect-context/UseOfflineDetectContext";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { useTagValues } from "../../queries/tag-values/UseTagValues";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { useForm  } from "react-hook-form";
import ManualEntryFormUtils, { ManualEntryFormDirtyType, ManualEntryFormMode, ManualEntryFormRefType, ManualEntryFormStateType, ManualEntryFormType, TagFormType, defaultManualEntryForm } from "./ManualEntryFormUtils";
import { Ref, forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { ObjectFeaturesEnum } from "../../common/enums/ObjectFeaturesEnum";
import { IKeyValuePair } from "../../common/models/KeyValuePair";
import FormPropertySection from "../../components/forms/form-property-section/FormPropertySection";
import FormInput from "../../components/forms/form-input/FormInput";
import Form from "../../components/forms/form/Form";
import FormProperty from "../../components/forms/form-property/FormProperty";
import { FormFeetAndInchesPicker } from "../../components/forms/form-feet-and-inches-picker/FormFeetAndInchesPicker";
import FormSelect from "../../components/forms/form-select/FormSelect";
import { LiquidProductionTATEnum } from "../../common/enums/LiquidProductionTATEnum";
import { ITagValue } from "../../common/models/ITagValue";
import { SetPointEventTypeEnum } from "../../common/enums/SetPointEventTypeEnum";
import { subscribe, unsubscribe } from "pubsub-js";
import { OFFLINE_PENDING_CHANGES_SAVED_TOPIC } from "../../common/models/pub-sub/PubSubTopics";
import LoadingZone from "../loading-zone/LoadingZone";
import OfflineDataService from "../../services/offline-data-service/OfflineDataService";
import FormTimeInput from "../forms/form-time-input/FormTimeInput";
import { getHoursMinutesSecondsText } from "../../common/utilities/HoursMinutesSeconds";
import { IeLynxObject } from "../../common/models/eLynxObject";
import Enumerable from "linq";
import { IEntityValueDeviceModel, IEntityValueTagModel } from "../../common/models/IEntityValueModels";
import _ from "lodash";
import FormFeetAndInchesMask from "../forms/form-feet-and-inches-mask/FormFeetAndInchesMaskInput";
import { getTotalFromFeetAndInchesText } from "../../common/utilities/FeetAndInches";
import { ALL_TAGS_CATEGORY_ID, ITagCategory, SUMMARY_LISTING_TAGS_CATEGORY_ID } from "../../common/models/ITagCategory";
import { useTranslation } from "react-i18next";
import ObjectTagValueUtils, { allTagsCategory } from "../object-tag-values/ObjectTagValuesUtils";
import { ContentContainer } from "../content-container/ContentContainer";
import { ContentSection } from "../content-section/ContentSection";
import NoData from "../no-data/NoData";
import { ViewOptionStateEnum } from "../contextual-app-bar/ViewOptionStateEnum";
import ManualEntryFormSidesheet from "./ManualEntryFormSidesheet";

export type ManualEntryFormProps = {
    mode: ManualEntryFormMode
    date: Date | undefined,
    formState: ManualEntryFormStateType,
    onFormStateChanged: (newFormState: ManualEntryFormStateType) => void;
    elynxObject?: IeLynxObject;
    writedownMode?: boolean;
    configurationID?: number; // Optional, used for write down configuration
    filteredTags?: IEntityValueTagModel[],
    setViewOptionState?: (state: ViewOptionStateEnum) => void;
};


function ManualEntryForm({
    mode,
    date,
    formState,
    onFormStateChanged,
    elynxObject,
    writedownMode,
    configurationID,
    filteredTags,
    setViewOptionState

}: ManualEntryFormProps, ref: Ref<unknown> | undefined) {

    const [filterText, setFilterText] = useState('');
    const [categories, setCategories] = useState<ITagCategory[]>([]);
    const [viewAllTags, setViewAllTags] = useState<boolean>(false);
    const [viewAllWriteTags, setViewAllWriteTags] = useState<boolean>(false);
    const [offlineState] = useOfflineDetectContext();    
    const routeContext = useRouteContext();
    const [userState] = useUserContext();
    const { t } = useTranslation();

    const offlineConfig = OfflineDataService.getOfflineConfig();

    const { data: dataResponse, refetch, status } = useQueryNotifications(useTagValues)({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number,
        objectID: elynxObject?.id ?? routeContext.contextObject!.id,
        objectType: elynxObject?.objectType ?? routeContext.contextObject!.objectType,
        configurationID: configurationID,
        date: date,
        offline: offlineState.offline,
        manualEntry: true,
        useLKVOffline: offlineConfig.useLKV
    });

    const { data: allDataResponse } = useQueryNotifications(useTagValues)({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number,
        objectID: elynxObject?.id ?? routeContext.contextObject!.id,
        objectType: elynxObject?.objectType ?? routeContext.contextObject!.objectType,
        configurationID: -1,
        date: date,
        offline: offlineState.offline,
        manualEntry: true,
        useLKVOffline: offlineConfig.useLKV
    });


    const form = useForm<ManualEntryFormType>({
        mode: "onChange",
         defaultValues: {...defaultManualEntryForm
        }
    });

    const {
        //handleSubmit,
        formState: {
            errors,
            isDirty,
            isValid,
            dirtyFields
        },
        clearErrors,
        getValues,
        //setValue,
        //watch,
        //control,
        reset,
        //trigger,
        //setError
    } = form;

    const tags = getValues('tags');


    const groupedTags = useMemo(() => {


        let allWriteTags: TagFormType[] = [];

        const model = ManualEntryFormUtils.mapToForm(mode, dataResponse, false, viewAllTags);

        if (viewAllWriteTags || configurationID === -1 || configurationID === undefined) {
        
            const feature = mode === ManualEntryFormMode.ManualEntry ? ObjectFeaturesEnum.ManualEntry : ObjectFeaturesEnum.Writedown;

            allWriteTags = tags.filter(v => (v.features & feature) > 0);
        }

        let filteredTags = Enumerable.from(model.tags)
            .join(dataResponse?.tags ?? [],
                o => o.id,
                v => v.id,
                (o, v) => o)
        .toArray();

        if (!viewAllTags) {

            const feature = mode === ManualEntryFormMode.ManualEntry ? ObjectFeaturesEnum.ManualEntry : ObjectFeaturesEnum.Writedown;

            filteredTags = filteredTags.filter(v => (v.features & feature) > 0);
        }

        filteredTags = Enumerable.from(filteredTags.concat(allWriteTags))
                            .distinct(o => o.id)
                            .toArray();

        filteredTags = Enumerable.from(tags).join(filteredTags,
            o => o.id,
            v => v.id,
            (o, v) => o)
            .toArray();


        const data = ManualEntryFormUtils.groupByCategory(filteredTags, categories, filterText);

        return data;

    },[
        mode,
        tags,
        configurationID,
        dataResponse,
        viewAllTags,
        viewAllWriteTags,
        categories,
        filterText
    ]);

    // const groupedTags = useMemo(() => {

    //     const data = tags ? ManualEntryFormUtils.groupByCategory(tags, categories, filterText) : [];

    //     if (viewAllTags) {
    //         return data;
    //     }

    //     const returnData: IKeyValuePair<string, TagFormType[]>[] = [];
    //     const feature = mode === ManualEntryFormMode.ManualEntry ? ObjectFeaturesEnum.ManualEntry : ObjectFeaturesEnum.Writedown;

    //     data.forEach(o => {
    //         const filteredTags = o.Value.filter(v => (v.features & feature) > 0);
    //         if (filteredTags.length > 0) {
    //             returnData.push({
    //                 Key: o.Key,
    //                 Value: filteredTags.sort((a, b) => a.name.localeCompare(b.name))
    //             });
    //         }
    //     });
        
    //     return returnData;
    // },[
    //     mode,
    //     tags,
    //     viewAllTags,
    //     categories,
    //     filterText
    // ]);


    function getModifiedValues() {

        const modifiedData = date ?? new Date();

        const timeStamp =  new Date(modifiedData.getFullYear(),
        modifiedData.getMonth(),
        modifiedData.getDate());

        let timestampString = timeStamp.toISOString();

        const newValues: ITagValue[] = [];

        const modifiedFields = dirtyFields as ManualEntryFormDirtyType;
    
        modifiedFields.tags?.forEach((x, ndx) => {

            const formTag = tags[ndx];
            
            if (x?.value) {

                if (formTag?.feetAndInchesText) {

                    const feetAndInchesValue = formTag.feetAndInchesText?.replace(/_/g, '0').trim() ?? '';
                    const parts = feetAndInchesValue.split("'");

                    const feet = parts[0].replace(/ /g, '');
                    const inches = parts[1]?.replace('"', '').replace(/ |_/g, '') ?? '0';

                    const feetAndInches = `${feet}' ${(+(inches)).toFixed(2)}"`;

                    let value = getTotalFromFeetAndInchesText(feetAndInches);
                    if (formTag.unitDisplayName.toLowerCase() === 'feet') {
                        value! /= 12;
                    }

                    const newTagValue: ITagValue = {
                        value: value,
                        displayText: feetAndInches,
                        setPointEventType: SetPointEventTypeEnum.Manual,
                        tagID: formTag.id,
                        unitOfMeasureID: formTag.uomID,
                        unitDisplayName: formTag.unitDisplayName,
                        timestamp: timestampString,
                    };

                    newValues.push(newTagValue);

                } else {

                    let value: number | undefined = formTag.value === null ? undefined : formTag.value; 
                    let displayText: string | undefined = formTag.value?.toString();

                    if (formTag.features && (formTag.features & ObjectFeaturesEnum.HoursMinutesSeconds) > 0) {
                        displayText = getHoursMinutesSecondsText(value)
                    }

                    const newTagValue: ITagValue = {
                        value: value,
                        displayText: displayText,
                        setPointEventType: SetPointEventTypeEnum.Manual,
                        tagID: formTag.id,
                        unitOfMeasureID: formTag.uomID,
                        unitDisplayName: formTag.unitDisplayName,
                        timestamp: timestampString,
                    };

                    newValues.push(newTagValue);
                }
            }
        });

        return newValues;
    }

    useImperativeHandle<unknown, ManualEntryFormRefType>(
        ref,
        () => ({
            // hasPendingChanges: () => { return isDirty; },
            // isValid: () => { return isValid },
            getChanges: getModifiedValues,
            getTags: () => {
                return dataResponse?.tags ?? [];
            }
        })
    );

    function _validateTankLevel(value: number, tag: TagFormType, tankHeightInches?: number): boolean | string {
        return tag.tatID === LiquidProductionTATEnum.TopLevel ?
            _validateTankTopLevel(value, tag, tankHeightInches) :
            _validateInterfaceLevelOpen(value, tag, tankHeightInches);
    }

    function _validateTankTopLevel(value: number, tag: TagFormType, tankHeightInches?: number): boolean | string {

        const topLevelIndex = tags.findIndex(o => o.id === tag.id);
        const interfaceLevelIndex = tags.findIndex(o => o.tatID === LiquidProductionTATEnum.InterfaceLevel && 
                                    o.objectID === tag.objectID && 
                                    o.objectTypeID === tag.objectTypeID);

        const interfaceLevel = getValues(`tags.${interfaceLevelIndex}.value`) ?? undefined;

        if (interfaceLevel !== undefined && !isNaN(value) && +interfaceLevel > +value) {
            return `The top level must be greater than or equal to the interface level`;
        }

        if (userState.userSettings?.tankLevelInches && value + (interfaceLevel ?? 0) > (tankHeightInches ?? 0)) {
            return `The top level plus the interface level cannot be greater than the tank height ${tankHeightInches} inches.`;
        }

        clearErrors(`tags.${topLevelIndex}.value`);
    
        return true;
    }

    function _validateInterfaceLevelOpen(value: number, tag: TagFormType, tankHeightInches?: number): boolean | string {

        const interfaceLevelIndex = tags.findIndex(o => o.id === tag.id);
        const topLevelIndex = tags.findIndex(o => o.tatID === LiquidProductionTATEnum.TopLevel && 
                                    o.objectID === tag.objectID && 
                                    o.objectTypeID === tag.objectTypeID);

        const topLevel = getValues(`tags.${topLevelIndex}.value`) ?? undefined;

        if (topLevel !== undefined && !isNaN(value) && +topLevel < +value) {
            return `The interface level must be less than or equal to the top level`;
        }

        if (userState.userSettings?.tankLevelInches && (topLevel ?? 0) + value > (tankHeightInches ?? 0)) {
            return `The top level plus the interface level cannot be greater than the tank height ${tankHeightInches} inches.`;
        }



        clearErrors(`tags.${interfaceLevelIndex}.value`);
    
        return true;
    }

    function _validateBounds(value: number | undefined, tag: TagFormType): boolean | string {

        if (mode === ManualEntryFormMode.WriteDown && (value === undefined || isNaN(value)) && tag.tagValue?.value !== undefined ) {
            return `A value is required for write down`
        }

        if (value !== undefined && !isNaN(value) && tag.writeLowerBound !== undefined && tag.writeLowerBoundOp) {

            if (!(0, eval)(`${value} ${tag.writeLowerBoundOp} ${tag.writeLowerBound}`)) {
                return `The value must be ${tag.writeLowerBoundOp} ${tag.writeLowerBound}`;
            }
        } 
        
        if (value !== undefined && !isNaN(value) && tag.writeUpperBound !== undefined && tag.writeUpperBoundOp) {

            if (!(0, eval)(`${value} ${tag.writeUpperBoundOp} ${tag.writeUpperBound}`)) {
                return `The value must be ${tag.writeUpperBoundOp} ${tag.writeUpperBound}`;
            }
        }

        return true;
    }

    function _validateGasAnalysisInput(): boolean | string {

        const tolerance = (dataResponse as IEntityValueDeviceModel).gasAnalysisTolerance ?? 0;

        // sum all gas analysis tag values, the total must not be more than 100% + tolerance.
        const gasAnalysisTags = getValues('tags').filter(o => (o.features & ObjectFeaturesEnum.GasComposition) > 0);

        let totalPercent: number = Enumerable.from(gasAnalysisTags).sum(o => +(o.value ?? 0));

        let maxDecimals = Enumerable.from(gasAnalysisTags).max(o => o.decimalPlaces ?? 0);
        const roundedValue = _.round(Math.abs(100 - totalPercent), maxDecimals);

        const invalid = roundedValue > tolerance;

        if (!invalid) {
            clearErrors('tags');
        }

        return invalid ? `The sum of all gas composition tags for this device must equal 100% +-(${tolerance}).` : true;
    }

    function renderInputControl(tag: TagFormType, _index: number) {

        const realIndex = tags.findIndex(o => o.id === tag.id);
        const writeable = mode === ManualEntryFormMode.WriteDown ? 
            (tag.features & ObjectFeaturesEnum.Writedown) > 0 : 
            (tag.features & ObjectFeaturesEnum.ManualEntry) > 0;

        return (
            <FormProperty key={`${tag.id}`}>
                {
                    !writeable ?
                    <FormInput
                        name={`tags.${realIndex}.value`}
                        sx={{
                            width: 1,
                            fontStyle: 'italic',
                            backgroundColor: userState.userSettings?.theme === 'dark' ? undefined : 'white'
                            
                        }}
                        backgroundColor={userState.userSettings?.theme === 'dark' ? undefined : 'white'}
                        readOnly={true}
                        numeric={true}
                        decimals={tag.decimalPlaces}
                        label={tag.name}
                        color ={(tag.features & ObjectFeaturesEnum.GasComposition) > 0 ? '#006400' : undefined}
                        helperText={<Stack
                            direction='row'
                            component='span'
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between'
                            }}>
                            <Box component='span'>{tag.unitDisplayName}</Box>
                            {mode === ManualEntryFormMode.WriteDown && <Box component='span'>{tag.timestamp}</Box>}
                        </Stack>}

                    />
                    :   
                tag.lookupList ? 

                        <FormSelect
                            name={`tags.${realIndex}.value`}
                            label={tag.name}
                            boldOnDirty={true}
                            sx={{
                                width: 1,
                            }}
                            helperText={<Stack
                                direction='row'
                                component='span'
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between'
                                }}>
                                <Box component='span'>{tag.unitDisplayName}</Box>
                                {mode === ManualEntryFormMode.WriteDown && <Box component='span'>{tag.timestamp}</Box>}
                            </Stack>}
                        >
                            <MenuItem value={-1} disabled={mode === ManualEntryFormMode.WriteDown}></MenuItem>
                            {tag.lookupList?.listText.map(o =>
                                <MenuItem key={o.id} value={+(o.value as string)}>{o.description}</MenuItem>
                            )}
                            
                        </FormSelect>
                

                    :
                ((tag.features ?? 0) & ObjectFeaturesEnum.HoursMinutesSeconds) > 0 ?
                            <FormTimeInput 
                                name={`tags.${realIndex}.value`}
                                label={tag.name}
                                sx={{
                                    width: 1
                                }}
                                />
                            :
                           
                ((tag.features ?? 0) & ObjectFeaturesEnum.FeetAndInches) > 0 ?
                 tag.tankStyle ?

                                !userState.userSettings?.tankLevelInches ?
                        <FormFeetAndInchesPicker
                            name={`tags.${realIndex}.value`}
                            label={tag.name}
                            maxFeet={tag.tankStyle ? (tag.tankStyle.heightInInches / 12) : 12}
                            boldOnDirty={true}
                            sx={{
                                width: 1,
                            }}
                             rules={{
                                 validate: (value: string) => _validateTankLevel(+value, tag)
                            }}
                            helperText={<Stack
                                direction='row'
                                component='span'
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-end'
                                }}>
                                {/* <Box>{tag.unitDisplayName}</Box> */}
                                {mode === ManualEntryFormMode.WriteDown && <Box component='span'>{tag.timestamp}</Box>}
                            </Stack>}

                            hideClear={mode === ManualEntryFormMode.WriteDown}
                        />
                        :
                        
                        <FormInput
                            name={`tags.${realIndex}.value`}
                            label={tag.name}
                            numeric={true}
                            sx={{
                                width: 1
                            }}
                            rules={{
                                //required: true,
                                validate: (val: number) => {return _validateTankLevel(val, tag, tag!.tankStyle!.heightInInches)} 
                            }}
                        />


                     :
                    <FormFeetAndInchesMask
                        name={`tags.${realIndex}.feetAndInchesText`}
                        label={tag.name}
                        sx={{
                            width: "100%",
                            "& .MuiFilledInput-root": {
                                alignItems: "baseline !important",
                            }
                        }}
                        //boldOnDirty
                        rules={{
                            validate: (value: string) => {
                                return ManualEntryFormUtils.validateFeetAndInches(value, tag);
                            }
                        }}
                    />

                     :
                     <FormInput
                        sx={{
                            width: 1,
                            
                        }}
                        name={`tags.${realIndex}.value`}
                        numeric={true}
                        decimals={mode === ManualEntryFormMode.WriteDown ? 99 : tag.decimalPlaces}
                        label={tag.name}
                        boldOnDirty={true}
                        color ={(tag.features & ObjectFeaturesEnum.GasComposition) > 0 ? '#006400' : undefined}
                        rules={{
                            validate: (value: number | null) => mode === ManualEntryFormMode.WriteDown && (tag.features & ObjectFeaturesEnum.GasComposition) > 0 ? 
                                            _validateGasAnalysisInput() : 
                                            _validateBounds(value === null ? undefined : +value, tag)
                        }}

                        helperText={<Stack
                            direction='row'
                            component='span'
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between'
                            }}>
                            <Box component='span'>{tag.unitDisplayName}</Box>
                            {mode === ManualEntryFormMode.WriteDown && <Box component='span'>{tag.timestamp}</Box>}
                        </Stack>}
                    />
                }
            </FormProperty>
        );
    }

    function renderTagGroup(item: IKeyValuePair<string, TagFormType[]>, index: number) {

        return (
            <FormPropertySection key={`${index}`} label={item.Key}>
                {item.Value.map((o, ndx) => renderInputControl(o, ndx))}
            </FormPropertySection>
        )
    }

    // function onCategoryChanged(
    //     event: React.SyntheticEvent,
    //     value: ITagCategory[],
    //     reason: AutocompleteChangeReason,
    //     details?: AutocompleteChangeDetails<ITagCategory> | undefined
    // ) {
    //     selectCategory(reason, details);
    // }

    function selectCategory(
        reason: AutocompleteChangeReason,
        details?: AutocompleteChangeDetails<ITagCategory> | undefined
    ) {


        const newCategories = [...categories];

        let item = categories.find(o => o.id === details?.option.id);

        if (item) {

            switch (reason) {
                case 'selectOption':
                    item.selected = true;

                    if (item.id === ALL_TAGS_CATEGORY_ID || item.id === SUMMARY_LISTING_TAGS_CATEGORY_ID) {
                        newCategories.forEach(o => {
                            if (o.id !== item?.id) {
                                o.selected = false;
                            }
                        });
                    } else {

                        newCategories.filter(o => o.id === ALL_TAGS_CATEGORY_ID || o.id === SUMMARY_LISTING_TAGS_CATEGORY_ID)
                            .forEach(o => o.selected = false);
                    }

                    break;

                case 'removeOption':
                    item.selected = false;
                    break;

                default:
                    break;
            }
        }

        if (!newCategories.find(o => o.selected)) {
            // if no categories are selected, select the all tags category
            const allTagsItem = newCategories.find(o => o.id === ALL_TAGS_CATEGORY_ID);
            if (allTagsItem) {
                allTagsItem.selected = true;
            }
        }

        setCategories(newCategories);
    }

    function resetOptions() {
        const newCategories = [...categories];
        newCategories.forEach(o => o.selected = false);
        const allTagsItem = newCategories.find(o => o.id === ALL_TAGS_CATEGORY_ID);
        if (allTagsItem) {
            allTagsItem.selected = true;
        }   
        setCategories(newCategories);
        setFilterText('');
    }

    useEffect(() => {

        if (allDataResponse) {
            const model = _.cloneDeep(allDataResponse);
            if (filteredTags && filteredTags.length > 0) {

                model.tags = Enumerable.from(model.tags)
                                .join(filteredTags,
                                    o => o.id,
                                    v => v.id,
                                    (o, v) => o)
                                    .toArray();
            }

            reset(ManualEntryFormUtils.mapToForm(mode, model, offlineState.offline, true));
        }

    }, [
        mode,
        allDataResponse,
        filteredTags,
        offlineState.offline,
        reset
    ]);


    useEffect(() => {
        if (writedownMode && dataResponse) {

            // if categories are provided, use them instead of the ones from dataResponse
            const categoryList = ObjectTagValueUtils.getTagCategories(dataResponse, false);

            const model = _.cloneDeep(dataResponse);
            if (filteredTags && filteredTags.length > 0) {

                model.tags = Enumerable.from(model.tags)
                                .join(filteredTags,
                                    o => o.id,
                                    v => v.id,
                                    (o, v) => o)
                                    .toArray();

            }


            const tags = model.tags.filter(o => (((o.features ?? ObjectFeaturesEnum.None) & ObjectFeaturesEnum.Writedown) > 0) || viewAllTags);
            let categoriesInUse: ITagCategory[] = [allTagsCategory];
            
            categoriesInUse = categoriesInUse.concat(Enumerable.from(categoryList)
                .join(tags,
                    o => o.id,
                    v => v.categoryID,
                    (o, v) => o
                ).distinct(o => o.id)
                .toArray());
            
            setCategories(categoriesInUse);
        }
    }, [
        dataResponse,
        writedownMode,
        filteredTags,
        setCategories,
        viewAllTags
    ]);    

    useEffect(() => {

        const valid = mode === ManualEntryFormMode.WriteDown ? Object.keys(errors).length === 0 : isValid;        

        if (formState.isDirty !== isDirty ||
            formState.isValid !== valid) {
            onFormStateChanged({
                isDirty,
                isValid: valid
            });
        }
    }, [
        formState,
        isDirty,
        isValid,
        errors,
        mode,
        onFormStateChanged
    ]);

    useEffect(() => {
        
        if (setViewOptionState) {
            setViewOptionState(categories.filter(o => o.selected)
                            .some(o => o.id !== ALL_TAGS_CATEGORY_ID) || filterText !== '' ?
                                ViewOptionStateEnum.Changed : 
                                ViewOptionStateEnum.Default); 
        }

    }, [
        setViewOptionState,
        categories,
        filterText
    ]);

    //console.log(`isDirty: ${isDirty} dirtyfields: ${JSON.stringify(dirtyFields)} and ${JSON.stringify(errors)}`)
    //const hasErrors = mode === ManualEntryFormMode.WriteDown ? Object.keys(dirtyFields).length && Object.keys(errors).length ? true : false : 
    

    useEffect(() => {
        const token = subscribe(OFFLINE_PENDING_CHANGES_SAVED_TOPIC, () => refetch());

        return () => {
            if (token) {
                unsubscribe(token);
            }
        }
    }, [
        refetch
    ]);

    return (

        <ContentContainer>
            <ContentSection>


        <LoadingZone isLoading={status === 'loading'}>
                    
            <Form form={form} sx={{ padding: 1 }}>
            {
                groupedTags.map((o, index) => renderTagGroup(o, index))
            }
           
           {groupedTags.length === 0 &&
                               
                <Box sx={{
                    padding: 1
                }}>
                    <NoData text={t('livedata.noTags')} />
                </Box>
                               
           }

            </Form>

        </LoadingZone>
        </ContentSection>
            {
            writedownMode && 

            <ManualEntryFormSidesheet
                resetOptions={resetOptions}
                configurationEnabled={true}
                categories={categories}
                selectCategory={selectCategory}
                filterText={filterText}
                setFilterText={setFilterText}
                showAllTagsOption={true}
                viewAllTags={viewAllTags}
                setViewAllTags={setViewAllTags}
                viewAllWriteTags={viewAllWriteTags}
                setViewAllWriteTags={setViewAllWriteTags}
                showAllWriteTagsOption={configurationID !== -1}
            />



            // <OptionsSideSheet onReset={() => resetOptions()}>

            // <SideSheetMultiSelect<ITagCategory>
            //         id="filter-tags"
            //         label={configurationEnabled
            //             ? t('objectTagValues.filterTagGroups')
            //             : t('category.title')
            //         }
            //         placeholder={configurationEnabled
            //             ? t('objectTagValues.tagGroups')
            //             : t('category.placeholder')
            //         }
            //         options={categories}
            //         isOptionEqualToValue={(option, value) => option.id === value.id}
            //         value={categories.filter(o => o.selected)} 
            //         getOptionLabel={(option) => option.text}
            //         onChange={onCategoryChanged}
            //     />

            //     <SideSheetFilterInput
            //         label={t("setpointAdmin.filterBy")}
            //         value={filterText}
            //         onChange={(value: string) => {
            //             setFilterText(value);
            //         }}
            //     />                                    


            //     {configurationID !== -1 && <SideSheetSwitch
            //         label='View All Writable Tags'
            //         checked={viewAllTags}
            //         onChange={(checked: boolean) => {
            //             setViewAllTags(checked);
            //         }}
            //     />}


            // </OptionsSideSheet>
        }

    </ContentContainer>        
    );
}

export default forwardRef(ManualEntryForm);